import { useContext, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import { roundADOTQuantity } from '@atom/components/common/requests/adotQuantityUtilities';
import {
  fromADOTBaseReplenishment,
  getADOTReplenishmentInputsDisabled,
  getADOTReplenishmentTotals,
  multiplyADOTConversion,
  toADOTBaseReplenishment,
} from '@atom/components/common/requests/adotReplenishmentUtilities';
import { GET_ASSET_USAGE_SUMMARY } from '@atom/graph/asset';
import { UPDATE_REPLENISHMENT } from '@atom/graph/assetRequest';
import { Modal, Snackbar } from '@atom/mui';
import {
  Replenishment,
  ReplenishmentCreateInput,
} from '@atom/types/assetRequest';

import ViewRequestContext from '../ViewRequestContext';

import ADOTReplenishmentForm from './ADOTReplenishmentForm';

type ADOTEditReplenishmentViewProps = {
  replenishment: Replenishment;
  onCancel: () => void;
};

const ADOTEditReplenishmentView = ({
  replenishment,
  onCancel,
}: ADOTEditReplenishmentViewProps) => {
  const {
    activeRequest,
    handleGetActiveRequest,
    onAssetRefresh,
    onReplenishmentSaved,
  } = useContext(ViewRequestContext);

  const { purchaseUnit, orderedBase } =
    getADOTReplenishmentTotals(activeRequest);

  const initialInputs = fromADOTBaseReplenishment(replenishment, purchaseUnit);

  const [inputs, setInputs] =
    useState<Partial<ReplenishmentCreateInput>>(initialInputs);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const [updateReplenishment] = useMutation(UPDATE_REPLENISHMENT);

  const { data: usageData, loading: totalRemainingLoading } = useQuery(
    GET_ASSET_USAGE_SUMMARY,
    {
      variables: { assetId: activeRequest?.assetId },
      fetchPolicy: 'no-cache',
      skip: !activeRequest?.assetId,
    },
  );

  const totalRemaining = R.pathOr(
    0,
    ['assetUsageSummary', 'totalRemaining'],
    usageData,
  );

  const updateInput = (
    value: any,
    property: keyof ReplenishmentCreateInput,
  ) => {
    setInputs(prev => ({ ...prev, [property]: value }));
  };

  const onSave = async () => {
    // Only send fields the user changed so untouched values aren't re-rounded.
    const changes: Partial<ReplenishmentCreateInput> = R.pickBy(
      (_, property) => inputs?.[property] !== initialInputs?.[property],
      toADOTBaseReplenishment(inputs, purchaseUnit),
    );

    setConfirmOpen(false);
    setSaving(true);

    try {
      await updateReplenishment({
        variables: {
          input: {
            requestId: activeRequest.id,
            replenishmentId: replenishment.id,
            ...changes,
          },
        },
      });

      Snackbar.info({ message: 'Replenishment saved.' });

      if (onReplenishmentSaved) {
        onReplenishmentSaved(activeRequest);
        return;
      }

      handleGetActiveRequest();
      onCancel();
      onAssetRefresh?.();
    } catch (error) {
      Snackbar.error({ message: 'An error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const otherReceivedBase = (activeRequest?.replenishments ?? []).reduce(
    (acc, item) =>
      item.id === replenishment.id ? acc : acc + (item.quantityReceived ?? 0),
    0,
  );
  const newBaseQuantity =
    multiplyADOTConversion(inputs?.quantityReceived, purchaseUnit) ?? 0;
  const totalRemainingNegativeError =
    !totalRemainingLoading &&
    roundADOTQuantity(
      totalRemaining - (replenishment?.quantityReceived ?? 0) + newBaseQuantity,
    ) < 0;

  const saveDisabled =
    getADOTReplenishmentInputsDisabled(inputs, true) ||
    totalRemainingLoading ||
    totalRemainingNegativeError ||
    saving;

  return (
    <>
      <ADOTReplenishmentForm
        title={replenishment?.name}
        purchaseUnit={purchaseUnit}
        totalOrderedBase={orderedBase}
        totalReceivedBase={otherReceivedBase}
        inputs={inputs}
        updateInput={updateInput}
        alwaysShowCalculate
        quantityError={totalRemainingNegativeError}
        onConfirm={() => setConfirmOpen(true)}
        onCancel={onCancel}
        confirmText="Save"
        confirmDisabled={saveDisabled}
        dataCyPrefix="adotReplenishmentEdit"
      />
      <Modal
        open={confirmOpen}
        title="Save Replenishment?"
        confirmButtonText="Save"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={onSave}
        CancelButtonProps={{ 'data-cy': 'adotReplenishmentEditConfirmCancel' }}
        ConfirmButtonProps={{
          'data-cy': 'adotReplenishmentEditConfirmConfirm',
        }}
      >
        Saving will update the item's Total Remaining and Unit Cost.
      </Modal>
    </>
  );
};

export default ADOTEditReplenishmentView;
